import { useEffect, useRef, useState } from 'react'
import { TASHKENT, loadYandexMaps, type YMap, type YMaps } from '@dorixona/ui'
import { useMapKeySource } from '../api/mapsKey'

export interface MapPoint {
  lat: number
  lng: number
}

export interface MapViewProps {
  /** Подпись для читалки: что показывает карта. */
  label: string
  /** Выбранная точка; null — точки нет, карта смотрит на Ташкент. */
  point: MapPoint | null
  /** Есть — щелчок по карте ставит точку; нет — карта только показывает. */
  onPick?: (point: MapPoint) => void
  height?: number
}

/**
 * Булавка аптеки: капля цвета бренда с крестом. Разметка — своя (стили
 * `.ui-map-pin` в kit.css); `.ui-map-pin-at` ставит остриё капли ровно в точку.
 */
const PIN =
  '<div class="ui-map-pin-at"><span class="ui-map-pin"><svg viewBox="0 0 24 24" aria-hidden="true">' +
  '<path d="M10 4h4v6h6v4h-6v6h-4v-6H4v-4h6z"/></svg></span></div>'

/** Масштаб, на котором видны дома: сюда карта приближает найденную точку. */
const STREET_ZOOM = 16

/** Координаты — до шести знаков: это около 10 см, точнее не нужно. */
const round = (n: number) => Math.round(n * 1e6) / 1e6

type State = 'loading' | 'ready' | 'failed'

/**
 * Карта на Яндекс Картах (JS API 2.1, общий загрузчик в `@dorixona/ui`).
 * API подключается только там, где карта есть. Не загрузилась — так и
 * говорим, а координаты остаются в полях формы.
 */
export function MapView({ label, point, onPick, height = 240 }: MapViewProps) {
  const box = useRef<HTMLDivElement>(null)
  const api = useRef<YMaps | null>(null)
  const map = useRef<YMap | null>(null)
  const [state, setState] = useState<State>('loading')
  const keySource = useMapKeySource()
  // Последний обработчик и точка — в ссылках: карта создаётся один раз,
  // а форма перерисовывается на каждую букву.
  const pick = useRef(onPick)
  const latest = useRef(point)
  // Точка, которую карта уже показала сама (стартовая или щелчок), — её
  // масштаб не трогаем; пришедшую снаружи (поиск, геолокация) приближаем.
  const own = useRef<MapPoint | null>(null)
  useEffect(() => {
    pick.current = onPick
    latest.current = point
  })

  // Карта создаётся один раз на экран и убирается вместе с ним.
  useEffect(() => {
    let alive = true
    loadYandexMaps(keySource).then(
      (ymaps) => {
        if (!alive) return
        const start = latest.current
        own.current = start
        const view = new ymaps.Map(
          box.current as HTMLDivElement,
          {
            center: start ? [start.lat, start.lng] : TASHKENT,
            zoom: start ? 15 : 11,
            controls: ['zoomControl'],
          },
          { suppressMapOpenBlock: true },
        )
        // Колесо прокручивает страницу, а не карту: форма длинная.
        view.behaviors.disable('scrollZoom')
        view.events.add('click', (event) => {
          const handler = pick.current
          if (!handler) return
          const [lat, lng] = event.get('coords')
          own.current = { lat: round(lat), lng: round(lng) }
          handler(own.current)
        })
        api.current = ymaps
        map.current = view
        setState('ready')
      },
      () => {
        if (alive) setState('failed')
      },
    )
    return () => {
      alive = false
      map.current?.destroy()
      map.current = null
    }
  }, [keySource])

  // Точка поменялась (щелчок, ввод координат) — переставляем булавку.
  // Сравниваем числа, а не объект: форма собирает точку заново на каждую букву.
  const lat = point?.lat
  const lng = point?.lng
  useEffect(() => {
    if (state !== 'ready') return
    const ymaps = api.current as YMaps
    const view = map.current as YMap
    view.geoObjects.removeAll()
    if (lat === undefined || lng === undefined) return
    view.geoObjects.add(
      new ymaps.Placemark(
        [lat, lng],
        {},
        {
          iconLayout: ymaps.templateLayoutFactory.createClass(PIN),
          iconShape: { type: 'Circle', coordinates: [0, -25], radius: 18 },
        },
      ),
    )
    // При выборе точка из поиска или «Моё местоположение» приближается до
    // улиц, чтобы её можно было поправить щелчком; свой щелчок масштаб не трогает.
    const mine = own.current?.lat === lat && own.current.lng === lng
    const zoom = pick.current && !mine ? Math.max(view.getZoom(), STREET_ZOOM) : view.getZoom()
    view.setCenter([lat, lng], zoom, { duration: 200 })
  }, [state, lat, lng])

  return (
    <div
      ref={box}
      className="ui-map"
      role={onPick ? 'group' : 'img'}
      aria-label={label}
      aria-busy={state === 'loading'}
      data-state={state}
      data-pick={onPick ? true : undefined}
      style={{ height }}
    >
      {state === 'failed' ? <span className="ui-map-failed">Карта не загрузилась</span> : null}
    </div>
  )
}
